import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Grid,
  CircularProgress,
  Alert,
  TextField,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  CheckCircle as SuccessIcon,
  ErrorOutline as ErrorOutlineIcon,
  Schedule as ScheduleIcon,
  PlayArrow as ForceRunIcon,
  Replay as ReplayIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Widgets as WidgetsIcon,
  Search as SearchIcon,
  CloudUpload as CloudUploadIcon,
  Build as BuildIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';

import WidgetSnapshotDialog from './WidgetSnapshotDialog';
import { useWebSocket } from './WebSocketContext';
import { useTranslation } from 'react-i18next';
import CloseButton from './Common/CloseButton';

// Theme colors
const primaryColor = '#5757b0';
const hoverColor = '#7777d0';
const successColor = '#4caf50';
const errorColor = '#f44336';
const warningColor = '#ff9800';

// Column widths (px) - used for consistent alignment
const columnWidths = {
  timestamp: 180,
  type: 64,
  status: 68,
  context: 140,
  duration: 90,
  file: 220,
  details: 300
};
const tableMinWidth = Object.values(columnWidths).reduce((total, value) => total + value, 0);

// Compact UI sizes
const compactFontSize = '0.72rem';
const compactHeadFontSize = '0.78rem';
const compactIconSize = 18;
const compactCellPadding = '6px 8px';

const EMPTY_FILTERS = Object.freeze({});

const ActionsLogsDialog = ({ 
  open, 
  onClose, 
  title = "Actions Logs",
  caseid = null,
  timeevent_id = null,
  showFilters = true,
  maxHeight = '600px',
  maxLogs = 20,
  defaultShowAll = false,
  extraFilters = EMPTY_FILTERS
}) => {
  const { t } = useTranslation();
  const { ws } = useWebSocket();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: maxLogs
  });
  
  // Selection state
  const [selectedCaseId, setSelectedCaseId] = useState(caseid || '');
  const [selectedTimeventId, setSelectedTimeventId] = useState(timeevent_id || '');
  
  // Available options
  const [availableCases, setAvailableCases] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  
  const [localFilters, setLocalFilters] = useState({
    status: '',
    actionType: '',
    executionContext: ''
  });
  const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);
  const [snapshotContext, setSnapshotContext] = useState(null);

  const normalizedExtraFilters = (extraFilters && typeof extraFilters === 'object')
    ? extraFilters
    : EMPTY_FILTERS;

  const appliedExtraFilters = useMemo(() => {
    if (normalizedExtraFilters === EMPTY_FILTERS) {
      return EMPTY_FILTERS;
    }

    const filtered = Object.entries(normalizedExtraFilters).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

    return Object.keys(filtered).length ? filtered : EMPTY_FILTERS;
  }, [normalizedExtraFilters]);

  
  // Derive current page size based on context filters
  const effectiveLimit = useMemo(() => (
    (!selectedCaseId && !selectedTimeventId) ? Math.max(maxLogs, 50) : maxLogs
  ), [selectedCaseId, selectedTimeventId, maxLogs]);

  useEffect(() => {
    setPaginationInfo(prev => ({
      ...prev,
      pageSize: effectiveLimit
    }));
  }, [effectiveLimit]);

  // Fetch action logs with selected parameters
  const fetchActionLogs = useCallback((pageOverride) => {
    if (!open || ws?.readyState !== WebSocket.OPEN) return;

    const targetPage = pageOverride ?? page;

    setLoading(true);
    setError('');

    const message = {
      command: 'getActionLogs',
      limit: effectiveLimit,
      page: targetPage
    };

    // Add selected context filters
    if (selectedCaseId) message.caseid = selectedCaseId;
    if (selectedTimeventId) message.timeevent_id = selectedTimeventId;

    // Add user-selected filters
    if (localFilters.status) message.status = localFilters.status;
    if (localFilters.actionType) message.action_type = localFilters.actionType;
    if (localFilters.executionContext) message.execution_context = localFilters.executionContext;

    Object.entries(appliedExtraFilters).forEach(([key, value]) => {
      message[key] = value;
    });

    console.log('ActionsLogsDialog: Fetching logs with message:', message);
    ws.send(JSON.stringify(message));
  }, [open, ws, selectedCaseId, selectedTimeventId, localFilters, appliedExtraFilters, effectiveLimit, page]);

  const handlePageChange = useCallback((targetPage) => {
    if (targetPage === undefined || targetPage === null) {
      return;
    }
    const numericPage = Number(targetPage);
    if (Number.isNaN(numericPage)) {
      return;
    }
    const safePage = Math.max(1, numericPage);
    const cappedPage = paginationInfo.totalPages ? Math.min(safePage, paginationInfo.totalPages) : safePage;
    setPage(cappedPage);
    setPageInput(String(cappedPage));
    fetchActionLogs(cappedPage);
  }, [fetchActionLogs, paginationInfo.totalPages]);

  const handlePageJump = useCallback(() => {
    const parsed = parseInt(pageInput, 10);
    if (Number.isNaN(parsed)) {
      setPageInput(String(page));
      return;
    }
    handlePageChange(parsed);
  }, [handlePageChange, pageInput, page]);

  // Initialize when dialog opens
  useEffect(() => {
    if (open) {
      const initialLimit = (!caseid && !timeevent_id) ? Math.max(maxLogs, 50) : maxLogs;
      setSelectedCaseId(caseid || '');
      setSelectedTimeventId(timeevent_id || '');
      setError('');
      setLogs([]);
      setPage(1);
      setPageInput('1');
      setPaginationInfo(prev => ({
        ...prev,
        page: 1,
        totalPages: 1,
        totalCount: 0,
        pageSize: initialLimit
      }));
      
      // Fetch options
      if (ws?.readyState === WebSocket.OPEN) {
        setLoadingOptions(true);
        console.log('ActionsLogsDialog: Fetching cases...');
        ws.send(JSON.stringify({ command: 'getcases' }));
        
        // Initial fetch after a short delay
        setTimeout(() => {
          const message = {
            command: 'getActionLogs',
            limit: initialLimit,
            page: 1
          };
          
          // Add initial context if provided
          if (caseid) message.caseid = caseid;
          if (timeevent_id) message.timeevent_id = timeevent_id;
          Object.entries(appliedExtraFilters).forEach(([key, value]) => {
            message[key] = value;
          });
          
          console.log('ActionsLogsDialog: Initial fetch with message:', message);
          ws.send(JSON.stringify(message));
          setLoading(true);
        }, 100);
      }
    }
  }, [open, caseid, timeevent_id, ws, maxLogs, appliedExtraFilters]);
  
  // Clear timeevent input when case selection changes
  useEffect(() => {
    if (open && selectedCaseId) {
      // Clear timeevent ID when case changes
      setSelectedTimeventId('');
      setPage(1);
      setPageInput('1');
    }
  }, [selectedCaseId, open]);
  
  // Note: Auto-fetch removed to prevent multiple requests
  // Users should use "Apply Filters" or "Show All Actions" button to trigger fetches

  // Listen for WebSocket messages
  useEffect(() => {
    if (!ws) return;

    const handleMessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        if (message.command === 'getActionLogs') {
          console.log('ActionsLogsDialog: Received response (handler ID:', Math.random().toString(36).substr(2, 9), '):', message);
          setLoading(false);
          if (message.data.status === 'success') {
            const receivedLogs = message.data.logs || [];
            console.log('ActionsLogsDialog: Setting logs count:', receivedLogs.length);
            const totalCount = message.data.total_count ?? receivedLogs.length;
            const responsePage = message.data.page ?? 1;
            const rawPageSize = message.data.page_size ?? message.data.limit ?? (receivedLogs.length || maxLogs);
            const safePageSize = rawPageSize && rawPageSize > 0 ? rawPageSize : maxLogs;
            const totalPages = message.data.total_pages ?? Math.max(1, Math.ceil(totalCount / safePageSize));

            setLogs(receivedLogs);
            setPaginationInfo({
              page: responsePage,
              totalPages,
              totalCount,
              pageSize: safePageSize
            });
            setPage(responsePage);
            setPageInput(String(responsePage));
            setError('');
          } else {
            console.error('ActionsLogsDialog: Error response:', message.data.message);
            setError(message.data.message || 'Failed to load action logs');
            setLogs([]);
          }
        } else if (message.command === 'getcases' || message.command === 'listcases') {
          console.log('ActionsLogsDialog: Received cases response:', message);
          setLoadingOptions(false);
          const casesPayload = message.data?.cases || message.cases;
          if (Array.isArray(casesPayload)) {
            console.log('ActionsLogsDialog: Setting available cases:', casesPayload.length);
            setAvailableCases(casesPayload);
          } else {
            console.warn('ActionsLogsDialog: No cases array in response:', message);
          }
        }
      } catch (err) {
        console.error('Error parsing WebSocket message:', err);
      }
    };

    ws.addEventListener('message', handleMessage);
    return () => ws.removeEventListener('message', handleMessage);
  }, [ws, maxLogs]);

  const handleFilterChange = (filterType, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const applyFilters = () => {
    handlePageChange(1);
  };

  const clearFilters = () => {
    setLocalFilters({
      status: '',
      actionType: '',
      executionContext: ''
    });
    setTimeout(() => handlePageChange(1), 100);
  };

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'SUCCESS':
        return <SuccessIcon sx={{ color: successColor, fontSize: compactIconSize }} />;
      case 'FAILED':
      case 'ERROR':
        return <ErrorOutlineIcon sx={{ color: errorColor, fontSize: compactIconSize }} />;
      case 'SKIPPED':
        return <ScheduleIcon sx={{ color: warningColor, fontSize: compactIconSize }} />;
      default:
        return <ScheduleIcon sx={{ color: '#999', fontSize: compactIconSize }} />;
    }
  };

  const getStatusChip = (status) => {
    const statusUpper = status?.toUpperCase();
    let color = 'default';
    
    switch (statusUpper) {
      case 'SUCCESS':
        color = 'success';
        break;
      case 'FAILED':
      case 'ERROR':
        color = 'error';
        break;
      case 'SKIPPED':
        color = 'warning';
        break;
      default:
        color = 'default';
        break;
    }

    return (
      <Chip
        label={status}
        color={color}
        size="small"
        icon={getStatusIcon(status)}
        sx={{ fontSize: compactFontSize }}
      />
    );
  };

  const renderStatusIndicator = (status) => {
    const normalized = (status || '').toLowerCase();
    if (['success', 'failed', 'error', 'skipped'].includes(normalized)) {
      return (
        <Tooltip title={status || 'Success'} arrow>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {getStatusIcon(status)}
          </Box>
        </Tooltip>
      );
    }
    return getStatusChip(status);
  };

  const renderLogDetails = (log, { allowWrap = false } = {}) => {
    const textClamp = allowWrap
      ? { whiteSpace: 'normal', wordBreak: 'break-word' }
      : { maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
    const status = (log.status || '').toLowerCase();
    const isSkipped = status === 'skipped' || status === 'cached';

    let detailContent = (
      <Typography variant="body2" sx={{ fontSize: compactFontSize, color: '#999' }}>
        No details
      </Typography>
    );

    if (log.error_details) {
      const detailColor = isSkipped ? warningColor : errorColor;
      detailContent = (
        <Tooltip title={log.error_details} arrow>
          <Typography
            variant="body2"
            sx={{
              fontSize: compactFontSize,
              color: detailColor,
              cursor: 'pointer',
              ...textClamp
            }}
          >
            {log.error_details}
          </Typography>
        </Tooltip>
      );
    } else if (log.success_details) {
      detailContent = (
        <Tooltip title={log.success_details} arrow>
          <Typography
            variant="body2"
            sx={{
              fontSize: compactFontSize,
              color: successColor,
              cursor: 'pointer',
              ...textClamp
            }}
          >
            {log.success_details}
          </Typography>
        </Tooltip>
      );
    }

    const snapshotType = log.widget_snapshot
      ? 'widget'
      : (log.timeevent_snapshot ? 'timeevent' : null);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {detailContent}
        {snapshotType && (
          <Button
            variant="text"
            size="small"
            onClick={() => handleOpenSnapshotDialog(log, snapshotType)}
            sx={{
              alignSelf: 'flex-start',
              textTransform: 'none',
              fontSize: compactFontSize,
              px: 0,
              minWidth: 0
            }}
          >
            {snapshotType === 'widget'
              ? t('actionsLogs.viewWidgetSnapshot', 'View widget snapshot')
              : t('actionsLogs.viewEventSnapshot', 'View event snapshot')}
          </Button>
        )}
      </Box>
    );
  };

  const renderMobileField = (label, content) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: '0.01em' }}>
        {label}
      </Typography>
      {content}
    </Box>
  );

  const getExecutionContextIcon = (context) => {
    switch (context) {
      case 'force_run':
        return <ForceRunIcon sx={{ fontSize: compactIconSize, color: primaryColor }} />;
      case 'automated_schedule':
        return <ScheduleIcon sx={{ fontSize: compactIconSize, color: '#666' }} />;
      case 'automated_replay':
        return <ReplayIcon sx={{ fontSize: compactIconSize, color: '#666' }} />;
      default:
        return null;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const normalizeTimestamp = (value) => {
      if (typeof value === 'number') return value;
      const raw = String(value).trim();
      if (!raw) return null;
      
      // Detect if timezone info already present (Z or +hh:mm / -hh:mm)
      const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw);
      if (hasTimezone) return raw;
      
      // Replace space between date/time with T for ISO compatibility
      const isoLike = raw.includes('T') ? raw : raw.replace(' ', 'T');
      return `${isoLike}Z`;
    };
    
    try {
      const normalized = normalizeTimestamp(timestamp);
      const date = normalized ? new Date(normalized) : new Date(timestamp);
      if (Number.isNaN(date.getTime())) {
        return timestamp;
      }
      return date.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
      });
    } catch {
      return timestamp;
    }
  };

  const formatDuration = (durationMs) => {
    if (!durationMs) return 'N/A';
    if (durationMs < 1000) {
      return `${Math.round(durationMs)}ms`;
    }
    return `${(durationMs / 1000).toFixed(1)}s`;
  };

  // Extract unique values for filters
  const uniqueActionTypes = [...new Set(logs.map(log => log.action_type).filter(Boolean))];
  const uniqueStatuses = [...new Set(logs.map(log => log.status).filter(Boolean))];
  const uniqueContexts = [...new Set(logs.map(log => log.execution_context).filter(Boolean))];

  const parseSnapshotPayload = (rawValue) => {
    if (!rawValue) return null;
    try {
      if (typeof rawValue === 'string') {
        return JSON.parse(rawValue);
      }
      return rawValue;
    } catch (err) {
      console.warn('ActionsLogsDialog: Failed to parse snapshot payload:', err);
      return null;
    }
  };

  const handleOpenSnapshotDialog = (log, snapshotType = 'widget') => {
    const rawSnapshot = snapshotType === 'widget'
      ? log.widget_snapshot
      : log.timeevent_snapshot;
    const parsed = parseSnapshotPayload(rawSnapshot);
    if (!parsed) {
      return;
    }
    setSnapshotContext({
      log,
      data: parsed,
      type: snapshotType
    });
    setSnapshotDialogOpen(true);
  };

  const handleCloseSnapshotDialog = () => {
    setSnapshotDialogOpen(false);
    setSnapshotContext(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            minHeight: maxHeight,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }
        }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${hoverColor} 100%)`,
          color: 'white',
          padding: '12px 18px',
          borderRadius: '16px 16px 0 0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AssignmentIcon sx={{ marginRight: 1, fontSize: compactIconSize }} />
          <Typography variant="h6" component="div" sx={{ fontSize: compactHeadFontSize }}>
            {title}
          </Typography>
        </Box>
        <CloseButton onClick={onClose} sx={{ color: 'white' }} />
      </DialogTitle>

      <DialogContent sx={{ padding: '12px' }}>
        {/* Display Mode and Filters Section */}
        <Box sx={{ marginBottom: 2 }}>
          {/* Context Selection */}
          <Box sx={{ marginBottom: 2 }}>
            <Typography variant="subtitle1" sx={{ marginBottom: 0.5, fontWeight: 600, fontSize: compactHeadFontSize }}>
              Context Selection
            </Typography>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Project/Case</InputLabel>
                  <Select
                    value={selectedCaseId}
                    label="Project/Case"
                    onChange={(e) => {
                      setSelectedCaseId(e.target.value);
                      setError('');
                      setLogs([]);
                      setPage(1);
                      setPageInput('1');
                    }}
                    disabled={loadingOptions}
                  >
                    <MenuItem value="">All Cases</MenuItem>
                    {availableCases.map(case_ => (
                      <MenuItem key={case_.id} value={case_.id}>
                        {case_.title || case_.name || `Case ${case_.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText sx={{ minHeight: '1.25rem' }}>&nbsp;</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="TimeEvent ID (optional)"
                  placeholder="Enter TimeEvent ID or leave empty for all"
                  value={selectedTimeventId}
                  onChange={(e) => {
                    setSelectedTimeventId(e.target.value);
                    setError('');
                    setLogs([]);
                    setPage(1);
                    setPageInput('1');
                  }}
                  helperText="Leave empty to show all timeevents in the selected project"
                />
              </Grid>
            </Grid>
            <Box sx={{ marginTop: 0.75, display: 'flex', gap: 0.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  console.log('ActionsLogsDialog: Clear Selection clicked');
                  setSelectedCaseId('');
                  setSelectedTimeventId('');
                  setError('');
                  setLogs([]);
                  setPage(1);
                  setPageInput('1');
                  // Trigger fetch of all actions after clearing selections
                  setTimeout(() => handlePageChange(1), 100);
                }}
                sx={{ 
                  borderRadius: '8px',
                  textTransform: 'none'
                }}
              >
                Clear Selection
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  console.log('ActionsLogsDialog: Apply Filters clicked - Current selections:', { selectedCaseId, selectedTimeventId });
                  handlePageChange(1);
                }}
                sx={{ 
                  backgroundColor: primaryColor,
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: hoverColor }
                }}
              >
                {(!selectedCaseId && !selectedTimeventId) ? 'Show All Actions' : 'Apply Filters'}
              </Button>
            </Box>

            <Box
              sx={{
                marginTop: 0.75,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center'
              }}
            >
              <Typography variant="body2" color="textSecondary">
                {`Page ${paginationInfo.page} of ${paginationInfo.totalPages} · ${paginationInfo.totalCount} total logs`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={page <= 1}
                  onClick={() => handlePageChange(page - 1)}
                  sx={{ borderRadius: '8px', textTransform: 'none' }}
                >
                  Previous
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={page >= paginationInfo.totalPages}
                  onClick={() => handlePageChange(page + 1)}
                  sx={{ borderRadius: '8px', textTransform: 'none' }}
                >
                  Next
                </Button>
              </Box>
              <TextField
                size="small"
                label="Jump to page"
                type="number"
                inputProps={{ min: 1 }}
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handlePageJump();
                  }
                }}
                sx={{ width: 160 }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handlePageJump}
                sx={{ borderRadius: '8px', textTransform: 'none' }}
              >
                Go
              </Button>
            </Box>
          </Box>

          {/* Filters Section */}
          {showFilters && (
            <Box>
              <Typography variant="subtitle1" sx={{ marginBottom: 1, display: 'flex', alignItems: 'center', fontWeight: 600, fontSize: compactHeadFontSize }}>
                <FilterIcon sx={{ marginRight: 1 }} />
                Filters
              </Typography>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={localFilters.status}
                      label="Status"
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {uniqueStatuses.map(status => (
                        <MenuItem key={status} value={status}>{status}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Action Type</InputLabel>
                    <Select
                      value={localFilters.actionType}
                      label="Action Type"
                      onChange={(e) => handleFilterChange('actionType', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {uniqueActionTypes.map(type => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Context</InputLabel>
                    <Select
                      value={localFilters.executionContext}
                      label="Context"
                      onChange={(e) => handleFilterChange('executionContext', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {uniqueContexts.map(context => (
                        <MenuItem key={context} value={context}>
                          {context.replace('_', ' ')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={applyFilters}
                      startIcon={<FilterIcon />}
                      sx={{ 
                        color: primaryColor, 
                        borderColor: primaryColor,
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: 'rgba(87, 87, 176, 0.05)',
                          borderColor: hoverColor
                        }
                      }}
                    >
                      Apply
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={clearFilters}
                      startIcon={<RefreshIcon />}
                      sx={{ 
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.05)'
                        }
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>

        {/* Content Section */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        ) : logs.length === 0 ? (
          <Box sx={{ textAlign: 'center', padding: 4 }}>
            <AssignmentIcon sx={{ fontSize: 48, color: '#ccc', marginBottom: 1.5 }} />
              <Typography variant="h6" color="textSecondary" sx={{ fontSize: compactHeadFontSize }}>
              No action logs found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {!selectedCaseId && !selectedTimeventId
                ? 'No action executions found across all actions.' 
                : 'No action executions match the current filters.'}
            </Typography>
          </Box>
        ) : isSmallScreen ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {logs.map((log, index) => (
              <Paper
                key={log.id || index}
                elevation={0}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '10px',
                  padding: '12px',
                  backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: compactFontSize, fontWeight: 600 }}>
                    {formatTimestamp(log.timestamp)}
                  </Typography>
                  {renderStatusIndicator(log.status)}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                  {renderMobileField(
                    'Type',
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {getActionTypeIcon(log.action_type)}
                      <Typography variant="body2" sx={{ fontSize: compactFontSize }}>
                        {log.action_type ? log.action_type.replace(/_/g, ' ') : 'N/A'}
                      </Typography>
                    </Box>
                  )}

                  {renderMobileField(
                    'Context',
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {getExecutionContextIcon(log.execution_context)}
                      <Typography variant="body2" sx={{ fontSize: compactFontSize }}>
                        {log.execution_context?.replace(/_/g, ' ') || 'N/A'}
                      </Typography>
                    </Box>
                  )}

                  {renderMobileField(
                    'Duration',
                    <Typography variant="body2" sx={{ fontSize: compactFontSize }}>
                      {formatDuration(log.duration_ms)}
                    </Typography>
                  )}

                  {renderMobileField(
                    'File',
                    <Typography variant="body2" sx={{ fontSize: compactFontSize, wordBreak: 'break-word' }}>
                      {log.file_source || 'N/A'}
                    </Typography>
                  )}

                  {renderMobileField('Details', renderLogDetails(log, { allowWrap: true }))}
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer 
            component={Paper} 
            elevation={0} 
            sx={{ 
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              overflow: 'hidden',
              overflowX: 'auto'
            }}
          >
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: tableMinWidth, fontSize: compactFontSize }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: columnWidths.timestamp, padding: compactCellPadding, fontSize: compactHeadFontSize }}>Timestamp</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: columnWidths.type, padding: compactCellPadding, fontSize: compactHeadFontSize, textAlign: 'center' }} align="center">Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: columnWidths.status, textAlign: 'center', padding: compactCellPadding, fontSize: compactHeadFontSize }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: columnWidths.context, padding: compactCellPadding, fontSize: compactHeadFontSize }}>Context</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: columnWidths.duration, textAlign: 'right', padding: compactCellPadding, fontSize: compactHeadFontSize }} align="right">Duration</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: columnWidths.file, padding: compactCellPadding, fontSize: compactHeadFontSize }}>File</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: columnWidths.details, padding: compactCellPadding, fontSize: compactHeadFontSize }}>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log, index) => (
                  <TableRow
                    key={log.id || index}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                      '&:hover': { backgroundColor: '#f0f0ff' }
                    }}
                  >
                    <TableCell sx={{ width: columnWidths.timestamp, verticalAlign: 'middle' }}>
                      <Tooltip title={formatTimestamp(log.timestamp)} arrow>
                        <Typography variant="body2" sx={{ fontSize: compactFontSize, cursor: 'help' }}>
                          {formatTimestamp(log.timestamp)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: columnWidths.type, verticalAlign: 'middle', textAlign: 'center' }} align="center">
                      <Tooltip title={log.action_type || 'Action type'} arrow>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          {getActionTypeIcon(log.action_type)}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: columnWidths.status, verticalAlign: 'middle', textAlign: 'center' }} align="center">
                      {renderStatusIndicator(log.status)}
                    </TableCell>
                    <TableCell sx={{ width: columnWidths.context, verticalAlign: 'middle' }}>
                      <Tooltip title={log.execution_context?.replace('_', ' ') || 'No context'} arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
                          {getExecutionContextIcon(log.execution_context)}
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              marginLeft: 1, 
                              fontSize: compactFontSize,
                              maxWidth: 100, 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap' 
                            }}
                          >
                            {log.execution_context?.replace('_', ' ')}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: columnWidths.duration, verticalAlign: 'middle', textAlign: 'right' }} align="right">
                      <Tooltip title={`Duration: ${formatDuration(log.duration_ms)}`} arrow>
                        <Typography variant="body2" sx={{ fontSize: compactFontSize, cursor: 'help' }}>
                          {formatDuration(log.duration_ms)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: columnWidths.file, verticalAlign: 'middle' }}>
                      <Tooltip title={log.file_source || 'No file source'} arrow>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: compactFontSize, 
                            maxWidth: 150, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            cursor: 'help' 
                          }}
                        >
                          {log.file_source || 'N/A'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: columnWidths.details, verticalAlign: 'middle' }}>
                      {renderLogDetails(log)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        padding: '12px', 
        background: 'rgba(247, 247, 255, 0.3)',
        borderTop: '1px solid rgba(87, 87, 176, 0.1)',
        justifyContent: 'space-between'
      }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
          {`Page ${paginationInfo.page} / ${paginationInfo.totalPages} · Showing ${logs.length} of ${paginationInfo.totalCount || logs.length} logs (page size: ${paginationInfo.pageSize})`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={() => {
              console.log('ActionsLogsDialog: Refresh clicked');
              handlePageChange(page);
            }}
            startIcon={<RefreshIcon />}
            variant="outlined"
            sx={{ 
              color: 'rgba(87, 87, 176, 0.8)',
              borderColor: 'rgba(87, 87, 176, 0.3)',
              borderRadius: '8px',
              px: 2.5,
              py: 0.5,
              fontWeight: '500',
              textTransform: 'none',
              '&:hover': { 
                backgroundColor: 'rgba(87, 87, 176, 0.05)',
                borderColor: 'rgba(87, 87, 176, 0.5)',
              }
            }}
          >
            Refresh
          </Button>
        </Box>
      </DialogActions>
      </Dialog>
      
      <WidgetSnapshotDialog
        open={snapshotDialogOpen}
        onClose={handleCloseSnapshotDialog}
        snapshotContext={snapshotContext}
        formatTimestamp={formatTimestamp}
      />
    </>
  );
};

const getActionTypeIcon = (actionType) => {
  if (!actionType) return <DescriptionIcon sx={{ fontSize: compactIconSize, color: '#999' }} />;
  const type = (actionType || '').toLowerCase();
  switch (true) {
    case /widget/.test(type):
      return <WidgetsIcon sx={{ fontSize: compactIconSize, color: primaryColor }} />;
    case /scan|search|analysis|analyze/.test(type):
      return <SearchIcon sx={{ fontSize: compactIconSize, color: '#1565c0' }} />;
    case /upload|ingest|import/.test(type):
      return <CloudUploadIcon sx={{ fontSize: compactIconSize, color: '#1565c0' }} />;
    case /transform|process|parse|extract/.test(type):
      return <BuildIcon sx={{ fontSize: compactIconSize, color: '#6a1b9a' }} />;
    default:
      return <DescriptionIcon sx={{ fontSize: compactIconSize, color: '#666' }} />;
  }
};

export default ActionsLogsDialog;
